import React from "react";
import { useTranslation } from "react-i18next";

import makeStyles from "@mui/styles/makeStyles";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";

import {
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineOppositeContent,
  TimelineNumberDot,
} from "./StyledTimelineComponents";

import type { GroupedMeasurement, GroupedPanel } from "../../workflowReducer";
import { DeleteButton } from "../../../shared/FormLayout/IconButtons/DeleteButton";
import { getSubAssetType } from "../../../shared/interfaces/SurveyableAsset";

interface PanelDetailsProps {
  measurement: GroupedMeasurement;
  onPanelDeleteButtonClick: (panel: GroupedPanel) => void;
  className?: string;
  children?: React.ReactNode[];
  editMode?: boolean;
}

const usePanelStyles = makeStyles((theme) => ({
  panelDetails: {
    width: "40%",
    justifyContent: "space-between",
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
  },
  dividerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    flexGrow: 1,
  },
  divider: {
    flex: "1 1 auto",
  },
  panelNo: {
    margin: theme.spacing(0, 1),
    fontSize: "1.1em",
  },
  actions: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}));

export function PanelTimelineItems(props: PanelDetailsProps) {
  const classes = usePanelStyles();

  const { t } = useTranslation();

  return (
    <div>
      {props.measurement.panels.map((panel) => (
        <div key={`${props.measurement.groupId}-${panel.panelAssetId ?? panel.panelNo}`}>
          <TimelineItem>
            <TimelineOppositeContent />
            <TimelineSeparator>
              <TimelineConnector />
              <TimelineNumberDot />
              <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent>
              <div className={classes.panelDetails}>
                <span>
                  <div>
                    <Typography variant="subtitle2" color="textSecondary" component="span">
                      {`${t("panels.panelNo", { val: getSubAssetType(props.measurement.parentAssetType) })}:`}
                    </Typography>
                    <Typography variant="subtitle2" component="span" className={classes.panelNo}>
                      {panel.panelNo}
                    </Typography>
                  </div>
                  <Typography variant="body2" component="span">
                    {panel.name}
                  </Typography>
                </span>
                {props.editMode && (
                  <>
                    <div className={classes.dividerContainer}>
                      <Divider className={classes.divider} />
                    </div>
                    <span className={classes.actions}>
                      <DeleteButton onClick={() => props.onPanelDeleteButtonClick(panel)} />
                    </span>
                  </>
                )}
              </div>
            </TimelineContent>
          </TimelineItem>
        </div>
      ))}
    </div>
  );
}
